import { z } from 'zod'
import { parseRequestError } from '@/common/utilities.ts'
import axios, { AxiosResponse } from 'axios'
import { RequestResponse } from '@/common/types_and_interfaces.ts'

interface DeleteRoleResponse {
  success: boolean
  message: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: null | any
}

export const RolesListSchema = z.object({
  id: z.number(),
  code: z.string().nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
})

const UserListSchema = z.object({
  id: z.number(),
  status: z.number(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  // phone: z.string().nullable(),
  email: z.string().nullable(),
  roles: z.string().nullable(),
  roles_list: z.array(RolesListSchema).nullable(),
})

type UserListStatusText = 'Active' | 'Inactive' | 'Pending'
export const getUserListStatusText = (status: number): UserListStatusText => {
  switch (status) {
    case 1:
      return 'Active'
    case 0:
      return 'Inactive'
    case 3:
      return 'Pending'
    default:
      return 'Active'
  }
}

export const UserListPaginatedSchema = z.object({
  current_page: z.number(),
  data: z.array(UserListSchema),
  first_page_url: z.string(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(
    z.union([
      z.object({ url: z.null(), label: z.string(), active: z.boolean() }),
      z.object({ url: z.string(), label: z.string(), active: z.boolean() }),
    ])
  ),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.number(),
  prev_page_url: z.string().nullable(),
  to: z.number().nullable(),
  total: z.number(),
})

export type UserList = z.infer<typeof UserListSchema>

export type SchemaUserListList = UserList[]
export type PaginatedUserLists = z.infer<typeof UserListPaginatedSchema>
export const getPaginatedProgramUserLists = async function (
  currentPage: number,
  programId?: number,
  recordsPerPage: number = 20,
  search?: string | undefined,
  status?: number | null,
  roles?: string[]
): Promise<PaginatedUserLists> {
  try {
    const response = await axios.get(`users/program/${programId}`, {
      params: {
        paginate: 1,
        roles_list: true,
        per_page: recordsPerPage,
        page: currentPage,
        search: search,
        roles: roles,
        status: status,
      },
    })
    return UserListPaginatedSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

const UserDetailsSchema = z.object({
  id: z.number(),
  status: z.number(),
  global_country_id: z.number().nullable(),
  global_state_id: z.number().nullable(),
  language_preference_id: z.number().nullable(),
  username: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  city: z.string().nullable(),
  profile_picture: z.string().nullable(),
  global_timezone_id: z.number().nullable(),
  callBackUrl: z.string().optional(),
})

export type UserDetails = z.infer<typeof UserDetailsSchema>

export const getUserDetail = async (userId?: string): Promise<UserDetails> => {
  try {
    const response = await axios.get(`users/${userId}`)
    return UserDetailsSchema.parse(response.data)
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

// update User
export type UpdateUserReq = {
  profile_picture_image?: File | string | null
  profile_picture?: File | string | null
  status?: number | null
  global_country_id?: number | null
  language_preference_id?: number | null
  global_state_id?: number | null
  first_name?: string
  last_name?: string
  city?: string
  global_timezone_id?: number | null
  phone?: string | null
  password?: string
}

const UpdateUserRespSchema = z.object({
  id: z.number(),
})

export type UpageUserResp = z.infer<typeof UpdateUserRespSchema>

export const UpdateUserData = async (
  requestBody: UpdateUserReq,
  id: number
): Promise<RequestResponse<UpageUserResp>> => {
  try {
    if (requestBody.profile_picture_image instanceof File) {
      requestBody.profile_picture_image = await convertFileToBase64(
        requestBody.profile_picture_image
      )
    }

    const response = await axios.put(`users/${id}`, requestBody)
    const cleanResponse = UpdateUserRespSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

export const getExportedData = async (
  status: number | null,
  search: string,
  program_id: number | null | undefined,
  roles: string[]
) => {
  try {
    const response = await axios.get(`users/export`, {
      responseType: 'blob',
      params: {
        status,
        program_id,
        roles,
        search,
      },
    })
    return response
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

const AcitivityLogListSchema = z.object({
  id: z.number(),
  log_type_id: z.number(),
  user_id: z.number(),
  user_type_id: z.number().nullable(),
  display_content: z.string().nullable(),
  created_datetime: z.string().nullable(),
})

export const AcitivityLogPaginatedSchema = z.object({
  current_page: z.number(),
  data: z.array(AcitivityLogListSchema),
  first_page_url: z.string(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(
    z.union([
      z.object({ url: z.null(), label: z.string(), active: z.boolean() }),
      z.object({ url: z.string(), label: z.string(), active: z.boolean() }),
    ])
  ),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.number(),
  prev_page_url: z.string().nullable(),
  to: z.number().nullable(),
  total: z.number(),
})

export type AcitivityLogList = z.infer<typeof AcitivityLogListSchema>

export type SchemaAcitivityLogListList = AcitivityLogList[]
export type PaginatedAcitivityLogLists = z.infer<
  typeof AcitivityLogPaginatedSchema
>
export const getPaginatedActivityLogLists = async function (
  currentPage: number,
  userId?: number,
  userType: number = 1,
  recordsPerPage: number = 20,
  search?: string | undefined,
  log_type?: string
): Promise<PaginatedAcitivityLogLists> {
  try {
    const params: {
      page: number
      user_id?: number
      user_type_id: number
      paginate: number
      per_page: number
      search?: string
      log_type?: string
    } = {
      user_id: userId,
      user_type_id: userType,
      paginate: 1,
      per_page: recordsPerPage,
      page: currentPage,
      ...(search && { search }),
      ...(log_type && { log_type }),
    }
    const response = await axios.get(`activity_logs`, {
      params,
    })
    return AcitivityLogPaginatedSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

export const deleteRole = async function (
  roleId: number
): Promise<AxiosResponse<DeleteRoleResponse>> {
  try {
    const response = await axios.delete<DeleteRoleResponse>(`roles/${roleId}`)
    return response
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}
